import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';

// Pages - Public
import LandingPage from './pages/LandingPage';
import ProductPage from './pages/ProductPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Pages - Dashboard
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import ConversationsPage from './pages/dashboard/ConversationsPage';
import FAQPage from './pages/dashboard/FAQPage';
import WorkflowsPage from './pages/dashboard/WorkflowsPage';
import AnalyticsPage from './pages/dashboard/AnalyticsPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import AccountPage from './pages/dashboard/AccountPage';
import BillingPage from './pages/dashboard/BillingPage';
import UsersPage from './pages/dashboard/UsersPage';
import AdminPage from './pages/dashboard/AdminPage';
import PageBuilderPage from './pages/dashboard/PageBuilderPage';
import TestLabPage from './pages/dashboard/TestLabPage';
import SupportPage from './pages/dashboard/SupportPage';
import AISetupPage from './pages/dashboard/AISetupPage';
import OnboardingPage from './pages/dashboard/OnboardingPage';
import ChannelsPage from './pages/dashboard/ChannelsPage';
import CalendarPage from './pages/dashboard/CalendarPage';
import { DemoModeProvider, useDemoMode } from './contexts/DemoModeContext';
import PipelineDebuggerPage from './pages/dashboard/PipelineDebuggerPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('threados_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('threados_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, organizationName) => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      organization_name: organizationName
    });
    const { access_token, user: userData, verification } = response.data;
    localStorage.setItem('threados_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Return both user and verification info for preview mode handling
    return { user: userData, verification };
  };

  const logout = () => {
    localStorage.removeItem('threados_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('threados_language') || 'en');
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    fetchTranslations();
  }, [language]);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations`);
      const transMap = {};
      response.data.forEach(t => {
        transMap[t.key] = t.translations[language] || t.translations['en'] || t.key;
      });
      setTranslations(transMap);
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    }
  };

  const t = (key, fallback = key) => translations[key] || fallback;

  const changeLanguage = (lang) => {
    localStorage.setItem('threados_language', lang);
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Super Admin Route
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Owner Only Route - For billing and plan management
const OwnerOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  // Only Owner and Super Admin can access
  if (user?.role !== 'owner' && !user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Calendar Route Guard — visible only in Demo Mode OR when a real calendar is connected.
const CalendarRouteGuard = ({ children }) => {
  const { active, hasRealCalendar, initialized } = useDemoMode();
  if (!initialized) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }
  if (!active && !hasRealCalendar) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Dashboard Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DemoModeProvider>
                  <DashboardLayout />
                </DemoModeProvider>
              </ProtectedRoute>
            }>
              <Route index element={<DashboardPage />} />
              <Route path="overview" element={<DashboardPage />} />
              <Route path="conversations" element={<ConversationsPage />} />
              <Route path="faq" element={<FAQPage />} />
              <Route path="workflows" element={<WorkflowsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="channels" element={<ChannelsPage />} />
              <Route path="calendar" element={
                <CalendarRouteGuard>
                  <CalendarPage />
                </CalendarRouteGuard>
              } />
              <Route path="account" element={<AccountPage />} />
              <Route path="billing" element={
                <OwnerOnlyRoute>
                  <BillingPage />
                </OwnerOnlyRoute>
              } />
              <Route path="users" element={<UsersPage />} />
              <Route path="admin" element={
                <SuperAdminRoute>
                  <AdminPage />
                </SuperAdminRoute>
              } />
              <Route path="page-builder" element={
                <SuperAdminRoute>
                  <PageBuilderPage />
                </SuperAdminRoute>
              } />
              <Route path="test-lab" element={
                <SuperAdminRoute>
                  <TestLabPage />
                </SuperAdminRoute>
              } />
              <Route path="admin/pipeline-debugger" element={
                <SuperAdminRoute>
                  <PipelineDebuggerPage />
                </SuperAdminRoute>
              } />
              <Route path="support" element={<SupportPage />} />
              <Route path="ai-setup" element={<AISetupPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
